import React, { useMemo, useState } from "react";
import { MigrationChange } from "@/hooks/useMigrationPreview";
import { SideBySideSectionDiffView } from "./SideBySideSectionDiffView";
import { IconProvider } from "@/components/ui";
import { Button } from "@/components/ui/button";

interface MigrationComparisonViewProps {
  currentStructure: any[];
  changes: MigrationChange;
  blueprintName: string;
  currentVersion: { major: number; minor: number; patch: number };
  targetVersion: { major: number; minor: number; patch: number };
  onSaveChanges?: (fieldUpdates: Map<string, Map<string, any>>) => Promise<void>;
}

export const MigrationComparisonView: React.FC<
  MigrationComparisonViewProps
> = ({
  currentStructure,
  changes,
  blueprintName,
  currentVersion,
  targetVersion,
  onSaveChanges,
}) => {
  // Track field changes made during migration preview
  const [fieldUpdates, setFieldUpdates] = useState<Map<string, Map<string, any>>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  
  // Check if there are any pending changes
  const hasPendingChanges = fieldUpdates.size > 0 && 
    Array.from(fieldUpdates.values()).some(sectionUpdates => sectionUpdates.size > 0);
  
  // Handler for field changes
  const handleFieldChange = (sectionId: string, fieldId: string, value: any) => {
    setFieldUpdates(prev => {
      const newUpdates = new Map(prev);
      const sectionUpdates = newUpdates.get(sectionId) || new Map();
      sectionUpdates.set(fieldId, value);
      newUpdates.set(sectionId, sectionUpdates);
      return newUpdates;
    });
  };
  
  // Handler for saving changes and applying migration
  const handleSave = async () => {
    if (!onSaveChanges) return;
    
    setIsSaving(true);
    try {
      // Pass the field updates directly as a map
      // This allows handleApplyMigration to apply them to the rebuilt structure
      // which includes newly added fields
      await onSaveChanges(fieldUpdates);
      setFieldUpdates(new Map()); // Clear pending changes after successful save
    } catch (error) {
      console.error('Failed to save migration changes:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const formatVersion = (version: {
    major: number;
    minor: number;
    patch: number;
  }) =>
    `${version.major}.${String(version.minor).padStart(3, "0")}.${String(
      version.patch
    ).padStart(3, "0")}`;

  // Build a map of field changes for quick lookup
  const fieldChangeMap = useMemo(() => {
    const map = new Map<
      string,
      { type: "added" | "removed" | "modified"; oldField?: any; newField?: any }
    >();

    changes.addedFields.forEach((field) => {
      map.set(field.fieldId, { type: "added" });
    });

    changes.removedFields.forEach((field) => {
      map.set(field.fieldId, { type: "removed" });
    });

    changes.modifiedFields.forEach((field) => {
      // For modified fields, we want to preserve the structure definitions
      // but the value will be added from the current field data in SideBySideSectionDiffView
      map.set(field.fieldId, {
        type: "modified",
        oldField: field.oldField || { 
          id: field.fieldId,
          label: field.oldFieldLabel, 
          type: field.oldFieldType 
        },
        newField: field.newField || { 
          id: field.fieldId,
          label: field.fieldLabel, 
          type: field.newFieldType 
        },
      });
    });

    return map;
  }, [changes]);

  // Determine change type for each section
  const getSectionChangeType = (
    sectionId: string
  ): "added" | "removed" | "modified" | "unchanged" => {
    if (changes.addedSections.some((s) => s.id === sectionId)) return "added";
    if (changes.removedSections.some((s) => s.id === sectionId))
      return "removed";
    if (changes.modifiedSections.some((s) => s.id === sectionId))
      return "modified";
    return "unchanged";
  };

  // Summary stats
  const totalChanges =
    changes.addedSections.length +
    changes.removedSections.length +
    changes.addedFields.length +
    changes.removedFields.length +
    changes.modifiedFields.length;

  return (
    <div className="space-y-6">
      {/* Header with version info and save button */}
      <div className="">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {`Migration preview for ${blueprintName} from version ${formatVersion(
              currentVersion
            )} to version ${formatVersion(targetVersion)}`}
          </p>
          
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="ml-4"
          >
            {isSaving 
              ? "Applying..." 
              : hasPendingChanges 
                ? "Apply Migration & Save Changes" 
                : "Apply Migration"}
          </Button>
        </div>
      </div>

      {/* Change Summary */}
      {changes.hasChanges && (
        <div className="">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <IconProvider iconstring="fa-plus" size={16} color="#16a34a" />
              <span className="text-sm text-green-600 dark:text-green-400">
                Fields Added: {changes.addedFields.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <IconProvider iconstring="fa-minus" size={16} color="#dc2626" />
              <span className="text-sm text-red-600 dark:text-red-400">
                Fields Removed: {changes.removedFields.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <IconProvider iconstring="fa-pencil" size={16} color="#f59e0b" />
              <span className="text-sm text-yellow-600 dark:text-yellow-400">
                Fields Modified: {changes.modifiedFields.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* No Changes Message */}
      {!changes.hasChanges && (
        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 text-center">
          <IconProvider iconstring="fa-info-circle" size={48} color="#3b82f6" />
          <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-400 mt-2">
            No Structural Changes
          </h3>
          <p className="text-sm text-blue-600 dark:text-blue-500 mt-1">
            The blueprint structure hasn't changed, but the version number will
            be updated to keep your object in sync.
          </p>
          <div className="mt-4 p-3 bg-white dark:bg-gray-900 rounded border border-blue-200 dark:border-blue-700">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Current:{" "}
              <span className="font-mono font-semibold">
                {formatVersion(currentVersion)}
              </span>{" "}
              → Target:{" "}
              <span className="font-mono font-semibold text-blue-600 dark:text-blue-400">
                {formatVersion(targetVersion)}
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Structure Comparison */}
      {changes.hasChanges && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Side-by-Side Comparison
          </h3>

          {/* Column Headers */}
          <div className="grid grid-cols-2 gap-px bg-gray-300 dark:bg-gray-600 rounded-lg overflow-hidden mb-4">
            <div className="bg-red-50 dark:bg-red-950/20 px-4 py-3 text-center">
              <div className="flex items-center justify-center gap-2">
                <IconProvider iconstring="fa-file" size={14} color="#dc2626" />
                <span className="font-semibold text-red-700 dark:text-red-400">
                  Current Structure
                </span>
                <span className="text-xs text-red-600 dark:text-red-400 mt-1">
                  Version {formatVersion(currentVersion)}
                </span>
              </div>
            </div>
            <div className="bg-green-50 dark:bg-green-950/20 px-4 py-3 text-center">
              <div className="flex items-center justify-center gap-2">
                <IconProvider iconstring="fa-file" size={14} color="#16a34a" />
                <span className="font-semibold text-green-700 dark:text-green-400">
                  After Migration
                </span>
                <span className="text-xs text-green-600 dark:text-green-400 mt-1">
                  Version {formatVersion(targetVersion)}
                </span>
              </div>
            </div>
          </div>

          {/* Current Structure Sections */}
          {currentStructure.map((section) => {
            const changeType = getSectionChangeType(section.id);
            const sectionFieldChanges = new Map<string, any>();

            // Build field change map for this section
            section.fields.forEach((field: any) => {
              const fieldChange = fieldChangeMap.get(field.id);
              if (fieldChange) {
                sectionFieldChanges.set(field.id, fieldChange);
              }
            });

            // Get added fields for this section with complete field definitions
            const sectionAddedFields = changes.addedFields
              .filter((f) => f.sectionId === section.id)
              .map((f) => ({
                fieldId: f.fieldId,
                fieldLabel: f.fieldLabel,
                fieldType: f.fieldType,
                field: f.field // Include complete field definition if available
              }));

            return (
              <SideBySideSectionDiffView
                key={section.id}
                section={section}
                changeType={changeType}
                fieldChanges={sectionFieldChanges}
                addedFields={sectionAddedFields}
                onFieldChange={handleFieldChange}
              />
            );
          })}

          {/* Show added sections that aren't in current structure */}
          {changes.addedSections.map((section) => (
            <SideBySideSectionDiffView
              key={section.id}
              section={{
                id: section.id,
                title: section.title,
                fields: [],
              }}
              changeType="added"
              fieldChanges={new Map()}
              addedFields={changes.addedFields
                .filter((f) => f.sectionId === section.id)
                .map((f) => ({
                  fieldId: f.fieldId,
                  fieldLabel: f.fieldLabel,
                  fieldType: f.fieldType,
                  field: f.field // Include complete field definition if available
                }))}
              onFieldChange={handleFieldChange}
            />
          ))}
        </div>
      )}
    </div>
  );
};
